import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl
} from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import SpinnerButton from '@/components/common/SpinnerButton';
import Error from '@/components/common/Error';

import { useCoachActivity } from '@/data/coachActivity';
import { useLocations } from '@/data/location';
import { useApplication } from '@/data/application';
import {
  useCoachActivityLocations,
  useBulkPostCoachActivityLocation
} from '@/data/coachActivityLocation';

const formSchema = z.object({
  locations: z
    .array(z.string())
    .min(1, { message: 'Must select at least 1 location' })
});

const APPLICATION_STEP_KEY = 'locations';

const CoachActivityLocationForm = () => {
  const params = useParams();
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const coachActivity = useCoachActivity(params.coachActivityId);
  const locations = useLocations({
    activityId: coachActivity.data.activity.id
  });
  const applicationStructure = useApplication({
    coachActivityId: params.coachActivityId
  });
  const coachActivityLocations = useCoachActivityLocations(
    params.coachActivityId
  );
  const bulkPostCoachActivityLocation = useBulkPostCoachActivityLocation();

  const groupedLocations = useMemo(() => {
    if (locations.isLoading && !locations.data) {
      return null;
    }
    return locations.data.reduce((acc, item) => {
      const regionName = item.region.name;
      if (!acc[regionName]) {
        acc[regionName] = [];
      }
      acc[regionName].push({ id: item.id, name: item.name });
      return acc;
    }, {});
  }, [locations.data, locations.isLoading]);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      locations: coachActivityLocations.data.map(
        (location) => location.locationId
      )
    }
  });

  const step = applicationStructure.data.steps.find(
    (step) => step.key === APPLICATION_STEP_KEY
  );

  const handleCheckboxChange = useCallback((locationId, field) => {
    return (checked) => {
      const newValue = checked
        ? [...field.value, locationId]
        : field.value.filter((value) => value !== locationId);
      field.onChange(newValue);
    };
  }, []);

  if (locations.isLoading && !locations.data) {
    return null;
  }

  const onSubmit = async (values) => {
    try {
      setSubmitting(true);
      setApiError('');
      const payload = values.locations.map((locationId) => ({
        locationId,
        coachActivityId: params.coachActivityId
      }));
      const updatedCoachActivityLocations = await bulkPostCoachActivityLocation(
        payload
      );
      await coachActivityLocations.mutate(updatedCoachActivityLocations, false);
      navigate(step.nextStepLink);
    } catch (err) {
      setApiError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const formErrors = form.formState.errors;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        {Object.entries(groupedLocations).map(([regionName, locations]) => (
          <div key={regionName} className="mb-8 space-y-1">
            <h3 className="mb-2">{regionName}</h3>
            <div className="grid gap-4">
              {locations.map((location) => (
                <FormField
                  key={location.id}
                  control={form.control}
                  name="locations"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center space-x-3 space-y-0 mb-2">
                      <FormControl>
                        <Checkbox
                          checked={field.value?.includes(location.id)}
                          onCheckedChange={handleCheckboxChange(
                            location.id,
                            field
                          )}
                        />
                      </FormControl>
                      <div className="leading-tight block">
                        <FormLabel>{location.name}</FormLabel>
                      </div>
                    </FormItem>
                  )}
                />
              ))}
              {formErrors.locations && (
                <span className="text-red-500">
                  {formErrors.locations.message}
                </span>
              )}
            </div>
          </div>
        ))}

        {apiError && <Error error={apiError} />}
        <div className="flex justify-end">
          <SpinnerButton loading={submitting}>Continue</SpinnerButton>
        </div>
      </form>
    </Form>
  );
};

export default CoachActivityLocationForm;
